import type {
  EstimatePricing,
  EstimateProductResponse,
} from '../../../../core/models/estimate-rating';

/** Time-window sort keys used for Express/Ground card ordering. */
export type DeliveryTimeBucket =
  'nineAm' | 'tenThirtyAm' | 'twelvePm' | 'endOfDay' | 'evening' | 'unknown';

/** Rated offering enriched with matrix display metadata. */
export interface DeliveryServiceOffering {
  productCode: string;
  deliveryDate: string;
  deliveryTime: string;
  transportationMode: 'A' | 'G' | string;
  guaranteed: boolean;
  pricing: EstimatePricing;
  discountedPricing?: EstimatePricing;
  couponDiscount?: {
    discountRate: number;
    discountType: string;
  };
  attestation?: string;
  /** Piece counts per SH code from the backend-rated `ratingInput.items`. */
  ratedSpecialHandlingPieceCounts?: Record<string, number>;
  displayName: string;
  /** Product Master `descEn` from products/selection. */
  descEn?: string;
  /** Product Master `descFr` from products/selection. */
  descFr?: string;
  /** Backend serviceTimeEnglish from products/selection (includes Guaranteed* when applicable). */
  serviceTimeEnglish?: string;
  /** Backend serviceTimeFrench from products/selection. */
  serviceTimeFrench?: string;
  /** Locale-resolved commitment line from backend serviceTime fields. */
  commitmentLabel: string;
  commitmentTimeKey: DeliveryTimeBucket;
  timeSortOrder: number;
  groupSortOrder: number;
}

/** One non-selectable arrival-date column with sorted service cards. */
export interface DeliveryDateColumn {
  deliveryDate: string;
  offerings: DeliveryServiceOffering[];
}

/** Line item shown under Additional Surcharges in the sticky panel. */
export interface EstimateBreakdownSurchargeLine {
  code: string;
  /** i18n surcharge key fragment; SH05–SH10 map to SPH (Special Handling). */
  labelCode: string;
  listAmount: string;
  discountedAmount?: string;
  displayMode: 'amount' | 'included';
  description?: string;
  /** i18n key for typed special-handling detail (e.g. Large package (1pc)). */
  descriptionKey?: string;
  descriptionParams?: Record<string, string>;
}

/** Optional inputs when building the sticky cost breakdown. */
export interface BuildEstimateBreakdownOptions {
  /** Piece counts per special-handling service code (SH05–SH10). */
  specialHandlingPieceCounts?: Record<string, number>;
  /** Backend-calculated discounted values. Eligibility must not be recalculated in the browser. */
  discountedPricing?: EstimatePricing;
  /** Applied percentage displayed beside lines whose backend value changed. */
  discountRate?: number;
}

/** Estimated-costs panel view model (shown as sticky sidebar at ≥1440px only). */
export interface EstimateBreakdownView {
  baseCost: string;
  discountedBaseCost?: string;
  discountRate?: number;
  additionalSurcharges: EstimateBreakdownSurchargeLine[];
  fuelSurcharge: string;
  discountedFuelSurcharge?: string;
  gstHst: string;
  discountedGstHst?: string;
  total: string;
  originalTotal?: string;
}

/** Estimate product plus optional transportation mode from PSA. */
export type EstimateOfferingInput = EstimateProductResponse & {
  transportationMode?: string;
  deliveryDate?: string;
  deliveryTime?: string;
  guaranteed?: boolean;
  /** Product Master English catalog name (`descEn`). */
  descEn?: string;
  /** Product Master French catalog name (`descFr`). */
  descFr?: string;
  /** Backend commitment label English (`serviceTimeEnglish`). */
  serviceTimeEnglish?: string;
  /** Backend commitment label French (`serviceTimeFrench`). */
  serviceTimeFrench?: string;
  discountedPricing?: EstimatePricing;
  couponDiscount?: {
    discountRate: number;
    discountType: string;
  };
  attestation?: string;
  /** Piece counts per SH code from the backend-rated `ratingInput.items`. */
  ratedSpecialHandlingPieceCounts?: Record<string, number>;
};

const TIME_BUCKET_META: Record<
  DeliveryTimeBucket,
  { sortOrder: number; expressSuffix: string; groundSuffix: string }
> = {
  nineAm: { sortOrder: 1, expressSuffix: ' 9 AM', groundSuffix: ' 9 AM' },
  tenThirtyAm: { sortOrder: 2, expressSuffix: ' 10:30 AM', groundSuffix: ' 10:30 AM' },
  twelvePm: { sortOrder: 3, expressSuffix: ' 12 PM', groundSuffix: ' 12 PM' },
  endOfDay: { sortOrder: 4, expressSuffix: '', groundSuffix: '' },
  evening: { sortOrder: 5, expressSuffix: ' Evening', groundSuffix: ' Evening' },
  unknown: { sortOrder: 99, expressSuffix: '', groundSuffix: '' },
};

/**
 * Maps an S4 delivery time string onto a known commitment bucket for sort and labels.
 */
export function resolveDeliveryTimeBucket(deliveryTime?: string): DeliveryTimeBucket {
  const normalized = (deliveryTime ?? '').trim().toUpperCase();

  if (!normalized) {
    return 'unknown';
  }

  if (normalized.includes('21') || normalized.includes('EVENING') || normalized === '9PM') {
    return 'evening';
  }

  if (
    normalized.includes('17') ||
    normalized.includes('18') ||
    normalized.includes('19') ||
    normalized.includes('EOD') ||
    normalized.includes('END OF DAY') ||
    normalized === 'STD'
  ) {
    return 'endOfDay';
  }

  if (normalized.includes('12')) {
    return 'twelvePm';
  }

  if (normalized.includes('10:30') || normalized.includes('1030')) {
    return 'tenThirtyAm';
  }

  if (normalized.includes('09') || normalized.startsWith('9')) {
    return 'nineAm';
  }

  return 'unknown';
}

/**
 * Builds a Figma-aligned service display name from transportation mode and time bucket.
 * Used only when products/selection `descEn` / `descFr` are unavailable.
 */
export function buildServiceDisplayName(
  transportationMode: string | undefined,
  timeBucket: DeliveryTimeBucket,
): string {
  const isGround = (transportationMode ?? '').toUpperCase() === 'G';
  const group = isGround ? 'Ground' : 'Express';
  const suffix = isGround
    ? TIME_BUCKET_META[timeBucket].groundSuffix
    : TIME_BUCKET_META[timeBucket].expressSuffix;
  return `${group}${suffix}`;
}

/**
 * Removes leading "Purolator " brand prefix from catalog service titles for card UI.
 * Leaves raw descEn/descFr on offerings unchanged; only affects display resolution.
 */
function stripBrandPrefix(name: string): string {
  return name.replace(/^Purolator\s+/i, '').trim();
}

/**
 * Resolves the card title from products/selection catalog copy, falling back to Express/Ground.
 * Prefers the active locale, then the other locale, then the synthesized mode+time name.
 * Strips leading "Purolator " from catalog names so cards match Figma (e.g. Express 9AM).
 */
export function resolveServiceDisplayName(
  input: {
    descEn?: string;
    descFr?: string;
    transportationMode?: string;
    deliveryTime?: string;
    commitmentTimeKey?: DeliveryTimeBucket;
  },
  locale: 'en' | 'fr' = 'en',
): string {
  const preferred = (locale === 'fr' ? input.descFr : input.descEn)?.trim();
  if (preferred) {
    return stripBrandPrefix(preferred);
  }

  const fallbackCatalog = (locale === 'fr' ? input.descEn : input.descFr)?.trim();
  if (fallbackCatalog) {
    return stripBrandPrefix(fallbackCatalog);
  }

  const timeBucket = input.commitmentTimeKey ?? resolveDeliveryTimeBucket(input.deliveryTime);
  return buildServiceDisplayName(input.transportationMode, timeBucket);
}

/**
 * Returns the backend service-time commitment label for the active locale as-is.
 * Prefers the active locale, then the other locale; empty when both are missing.
 */
export function resolveCommitmentLabel(
  input: {
    serviceTimeEnglish?: string;
    serviceTimeFrench?: string;
  },
  locale: 'en' | 'fr' = 'en',
): string {
  const preferred = (locale === 'fr' ? input.serviceTimeFrench : input.serviceTimeEnglish)?.trim();
  if (preferred) {
    return preferred;
  }

  return (locale === 'fr' ? input.serviceTimeEnglish : input.serviceTimeFrench)?.trim() ?? '';
}

/**
 * Formats a numeric money string as `$X.XX`.
 */
export function formatCurrencyAmount(value: string | number): string {
  const amount = typeof value === 'number' ? value : Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return '$0.00';
  }

  return `$${amount.toFixed(2)}`;
}

/**
 * Parses a total price for comparison; invalid values sort as Infinity.
 */
function parsePrice(value: string | undefined): number {
  const amount = Number.parseFloat(value ?? '');
  return Number.isNaN(amount) ? Number.POSITIVE_INFINITY : amount;
}

/**
 * Parses YYYY/MM/DD or YYYY-MM-DD into a sortable timestamp.
 */
function parseDeliveryDate(value: string): number {
  const normalized = value.replaceAll('-', '/');
  const [year, month, day] = normalized.split('/').map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return Number.POSITIVE_INFINITY;
  }

  return Date.UTC(year, month - 1, day);
}

/**
 * Converts a successful estimate into a matrix offering when pricing and date exist.
 */
function toOffering(
  input: EstimateOfferingInput,
  locale: 'en' | 'fr' = 'en',
): DeliveryServiceOffering | null {
  if (!input.pricing || !input.deliveryDate) {
    return null;
  }

  const transportationMode = (input.transportationMode ?? 'A').toUpperCase();
  const timeBucket = resolveDeliveryTimeBucket(input.deliveryTime);
  const guaranteed = input.guaranteed === true;
  const descEn = input.descEn?.trim() || undefined;
  const descFr = input.descFr?.trim() || undefined;
  const serviceTimeEnglish = input.serviceTimeEnglish?.trim() || undefined;
  const serviceTimeFrench = input.serviceTimeFrench?.trim() || undefined;

  return {
    productCode: input.productCode,
    deliveryDate: input.deliveryDate,
    deliveryTime: input.deliveryTime ?? '',
    transportationMode,
    guaranteed,
    pricing: input.pricing,
    discountedPricing: input.discountedPricing,
    couponDiscount: input.couponDiscount,
    attestation: input.attestation,
    ratedSpecialHandlingPieceCounts: input.ratedSpecialHandlingPieceCounts,
    descEn,
    descFr,
    serviceTimeEnglish,
    serviceTimeFrench,
    displayName: resolveServiceDisplayName(
      {
        descEn,
        descFr,
        transportationMode,
        deliveryTime: input.deliveryTime,
        commitmentTimeKey: timeBucket,
      },
      locale,
    ),
    commitmentLabel: resolveCommitmentLabel({ serviceTimeEnglish, serviceTimeFrench }, locale),
    commitmentTimeKey: timeBucket,
    timeSortOrder: TIME_BUCKET_META[timeBucket].sortOrder,
    groupSortOrder: transportationMode === 'G' ? 2 : 1,
  };
}

/**
 * Groups successful offerings into date columns (earliest first) with Express→Ground time sort.
 * @param locale Active UI locale so card titles use products/selection `descEn` / `descFr`.
 */
export function buildServiceMatrix(
  inputs: EstimateOfferingInput[],
  locale: 'en' | 'fr' = 'en',
): DeliveryDateColumn[] {
  const offerings = inputs
    .map((input) => toOffering(input, locale))
    .filter((item): item is DeliveryServiceOffering => item !== null);

  const byDate = new Map<string, DeliveryServiceOffering[]>();
  for (const offering of offerings) {
    const existing = byDate.get(offering.deliveryDate) ?? [];
    existing.push(offering);
    byDate.set(offering.deliveryDate, existing);
  }

  return [...byDate.entries()]
    .sort(([leftDate], [rightDate]) => parseDeliveryDate(leftDate) - parseDeliveryDate(rightDate))
    .map(([deliveryDate, dateOfferings]) => ({
      deliveryDate,
      offerings: [...dateOfferings].sort((left, right) => {
        if (left.groupSortOrder !== right.groupSortOrder) {
          return left.groupSortOrder - right.groupSortOrder;
        }

        if (left.timeSortOrder !== right.timeSortOrder) {
          return left.timeSortOrder - right.timeSortOrder;
        }

        return left.productCode.localeCompare(right.productCode);
      }),
    }));
}

/**
 * Counts distinct successful product codes available for comparison badges.
 * Fastest / Most economical labels only apply when the shipper can compare at least two services.
 */
function countDistinctSuccessfulProductCodes(inputs: EstimateOfferingInput[]): number {
  const codes = new Set<string>();
  for (const input of inputs) {
    if (toOffering(input) !== null) {
      codes.add(input.productCode);
    }
  }
  return codes.size;
}

/**
 * Returns the product code with the earliest delivery date and time window.
 * Returns null when fewer than two distinct services are available (comparison labels do not apply).
 */
export function resolveFastestProductCode(inputs: EstimateOfferingInput[]): string | null {
  if (countDistinctSuccessfulProductCodes(inputs) < 2) {
    return null;
  }

  const matrix = buildServiceMatrix(inputs);
  return matrix[0]?.offerings[0]?.productCode ?? null;
}

/**
 * Returns the product code with the lowest total price among successful offerings.
 * Returns null when fewer than two distinct services are available (comparison labels do not apply).
 */
export function resolveMostEconomicalProductCode(inputs: EstimateOfferingInput[]): string | null {
  const offerings = inputs
    .map((input) => toOffering(input))
    .filter((item): item is DeliveryServiceOffering => item !== null);

  if (offerings.length === 0 || countDistinctSuccessfulProductCodes(inputs) < 2) {
    return null;
  }

  return [...offerings].sort((left, right) => {
    const priceDelta = parsePrice(left.pricing.totalPrice) - parsePrice(right.pricing.totalPrice);
    if (priceDelta !== 0) {
      return priceDelta;
    }

    return left.productCode.localeCompare(right.productCode);
  })[0]!.productCode;
}

/**
 * Picks the End of Day offering on the earliest delivery date for default selection.
 */
export function pickDefaultEndOfDayOffering(
  inputs: EstimateOfferingInput[],
): DeliveryServiceOffering | null {
  const matrix = buildServiceMatrix(inputs);
  for (const column of matrix) {
    const endOfDay = column.offerings.find((item) => item.commitmentTimeKey === 'endOfDay');
    if (endOfDay) {
      return endOfDay;
    }
  }

  return matrix[0]?.offerings[0] ?? null;
}

/**
 * Builds sticky estimate breakdown rows from full and backend-authoritative discounted pricing.
 * Zero-amount accessorials render as Included; only changed values receive discount metadata.
 * OSNR (signature not required) is omitted — ESN does not show that line on the cost breakdown.
 * SH05–SH10 accessorials use the Special Handling label and typed detail copy.
 * When peakSurcharges is present, classified peak lines (e.g. Peak Additional Handling,
 * Peak Residential Heavy Weight) replace the aggregated peak row.
 */
export function buildEstimateBreakdown(
  pricing: EstimatePricing,
  options?: BuildEstimateBreakdownOptions,
): EstimateBreakdownView {
  const additionalSurcharges: EstimateBreakdownSurchargeLine[] = [];
  const discountedPricing = options?.discountedPricing;

  appendPeakSurchargeLines(additionalSurcharges, pricing, discountedPricing);

  if (parsePrice(pricing.surcharges.multiPiece) > 0) {
    additionalSurcharges.push(
      toChargeLine(pricing.surcharges.multiPiece, {
        code: 'multiPiece',
        discountedAmount: discountedPricing?.surcharges.multiPiece,
      }),
    );
  }

  if (parsePrice(pricing.surcharges.holiday) > 0) {
    additionalSurcharges.push(
      toChargeLine(pricing.surcharges.holiday, {
        code: 'holiday',
        discountedAmount: discountedPricing?.surcharges.holiday,
      }),
    );
  }

  for (const [index, accessorial] of pricing.accessorialCharges.entries()) {
    if (isOriginSignatureNotRequiredCode(accessorial.productCode)) {
      continue;
    }

    additionalSurcharges.push(
      toAccessorialChargeLine(
        accessorial,
        options?.specialHandlingPieceCounts,
        discountedPricing?.accessorialCharges[index]?.productCode === accessorial.productCode
          ? discountedPricing.accessorialCharges[index]?.amount
          : discountedPricing?.accessorialCharges.find(
              (charge) => charge.productCode === accessorial.productCode,
            )?.amount,
      ),
    );
  }

  const gst = parsePrice(pricing.taxes.GST);
  const hst = parsePrice(pricing.taxes.HST);
  const gstHst = (gst + hst).toFixed(2);
  const discountedGstHst = discountedPricing
    ? (parsePrice(discountedPricing.taxes.GST) + parsePrice(discountedPricing.taxes.HST)).toFixed(2)
    : undefined;

  return {
    baseCost: pricing.basePrice,
    ...(discountedValue(pricing.basePrice, discountedPricing?.basePrice)
      ? { discountedBaseCost: discountedPricing?.basePrice }
      : {}),
    ...(options?.discountRate !== undefined ? { discountRate: options.discountRate } : {}),
    additionalSurcharges,
    fuelSurcharge: pricing.fuelSurcharge,
    ...(discountedValue(pricing.fuelSurcharge, discountedPricing?.fuelSurcharge)
      ? { discountedFuelSurcharge: discountedPricing?.fuelSurcharge }
      : {}),
    gstHst,
    ...(discountedValue(gstHst, discountedGstHst) ? { discountedGstHst } : {}),
    total: discountedPricing?.totalPrice ?? pricing.totalPrice,
    ...(discountedValue(pricing.totalPrice, discountedPricing?.totalPrice)
      ? { originalTotal: pricing.totalPrice }
      : {}),
  };
}

const PEAK_RESIDUAL_EPSILON = 0.005;

/**
 * Appends classified peak lines from peakSurcharges when present; otherwise a single Peak row.
 * Residual aggregated peak (total − sum of details) keeps the generic Peak Surcharge label.
 */
function appendPeakSurchargeLines(
  additionalSurcharges: EstimateBreakdownSurchargeLine[],
  pricing: EstimatePricing,
  discountedPricing?: EstimatePricing,
): void {
  const peakDetails = (pricing.peakSurcharges ?? []).filter(
    (entry) => parsePrice(entry.amount) > 0 && entry.productCode.trim().length > 0,
  );
  const totalPeak = parsePrice(pricing.surcharges.peak);

  if (peakDetails.length === 0) {
    if (totalPeak > 0) {
      additionalSurcharges.push(
        toChargeLine(pricing.surcharges.peak, {
          code: 'peak',
          discountedAmount: discountedPricing?.surcharges.peak,
        }),
      );
    }
    return;
  }

  let detailsSum = 0;
  for (const entry of peakDetails) {
    const productCode = entry.productCode.trim().toUpperCase();
    const amount = parsePrice(entry.amount);
    detailsSum += amount;
    const labelCode = peakBreakdownLabelCode(productCode);
    const discountedMatch = discountedPricing?.peakSurcharges?.find(
      (charge) => charge.productCode.trim().toUpperCase() === productCode,
    )?.amount;
    additionalSurcharges.push(
      toChargeLine(entry.amount, {
        code: `peak${productCode}`,
        labelCode,
        discountedAmount: discountedMatch,
      }),
    );
  }

  const residual = totalPeak - detailsSum;
  if (residual > PEAK_RESIDUAL_EPSILON) {
    additionalSurcharges.push(
      toChargeLine(residual.toFixed(2), {
        code: 'peak',
        discountedAmount: undefined,
      }),
    );
  }
}

/** Accessorial product codes whose nested peak amount has a dedicated label (OLS parity). */
const CLASSIFIED_PEAK_PRODUCT_CODES: ReadonlySet<string> = new Set([
  'SH06',
  'SH07',
  'SH09',
  'SH10',
]);

/**
 * Maps a peak's accessorial product code to its i18n surcharge key.
 * Mirrors OLS, which labels each nested peak by the accessorial it is attached to
 * (e.g. Peak Additional Handling, Peak Residential Heavy Weight); other codes use the generic peak label.
 */
function peakBreakdownLabelCode(productCode: string): string {
  if (CLASSIFIED_PEAK_PRODUCT_CODES.has(productCode)) {
    return `peak${productCode}`;
  }
  return 'peak';
}

/** True when the accessorial product code is a special-handling service (SH05–SH10). */
function isSpecialHandlingAccessorialCode(code: string): boolean {
  return /^SH0[5-9]$|^SH10$/i.test(code.trim());
}

/**
 * True when the accessorial is OSNR (origin signature not required).
 * Hidden from the Estimated Costs breakdown to match ESN guest-shipper UX.
 */
function isOriginSignatureNotRequiredCode(code: string): boolean {
  return code.trim().toUpperCase() === 'OSNR';
}

/**
 * Maps an accessorial charge into a breakdown line, enriching SH05–SH10 display metadata.
 */
function toAccessorialChargeLine(
  accessorial: { productCode: string; amount: string; description?: string },
  specialHandlingPieceCounts?: Record<string, number>,
  discountedAmount?: string,
): EstimateBreakdownSurchargeLine {
  const code = accessorial.productCode.trim();
  if (isSpecialHandlingAccessorialCode(code)) {
    const normalized = code.toUpperCase();
    const pieceCount = specialHandlingPieceCounts?.[normalized] ?? 1;
    return toChargeLine(accessorial.amount, {
      code: normalized,
      labelCode: 'SPH',
      descriptionKey: `createShipment.serviceStep.deliveryService.breakdown.specialHandlingDetails.${normalized}`,
      descriptionParams: { count: String(Math.max(1, pieceCount)) },
      discountedAmount,
    });
  }

  return toChargeLine(accessorial.amount, {
    code,
    description: accessorial.description,
    discountedAmount,
  });
}

/**
 * Maps a charge amount into a breakdown line, including Included for zero amounts.
 */
function toChargeLine(
  amount: string,
  options?: {
    code?: string;
    labelCode?: string;
    description?: string;
    descriptionKey?: string;
    descriptionParams?: Record<string, string>;
    discountedAmount?: string;
  },
): EstimateBreakdownSurchargeLine {
  const code = options?.code ?? '';
  const line: EstimateBreakdownSurchargeLine = {
    code,
    labelCode: options?.labelCode ?? code,
    listAmount: amount,
    displayMode: parsePrice(amount) <= 0 ? 'included' : 'amount',
  };

  if (options?.description?.trim()) {
    line.description = options.description.trim();
  }

  if (options?.descriptionKey) {
    line.descriptionKey = options.descriptionKey;
    line.descriptionParams = options.descriptionParams;
  }

  if (discountedValue(amount, options?.discountedAmount)) {
    line.discountedAmount = options?.discountedAmount;
  }

  return line;
}

/** Returns true only when the backend supplied a meaningfully different discounted amount. */
function discountedValue(original: string, discounted?: string): discounted is string {
  return discounted !== undefined && parsePrice(original) !== parsePrice(discounted);
}
